// src/views/menage/ControleBilan.jsx — « LE BILAN », la vue neuve de l'écran
// Contrôle (maquette validée Dimitri 2026-08-21). L'écran « À contrôler »
// reste l'outil de terrain ; ICI, la page gagne sa raison d'exister :
//
//   · BANDEAU : « Le bilan » + mois avec flèches (comme l'historique des
//     demandes), trois blocs translucides (contrôles / rien à signaler avec
//     le % / à reprendre avec l'en-attente) ;
//   · « CE QUI REVIENT » — LE CŒUR : les catégories signalées, triées, en
//     barres terracotta dégradées, et LA PHRASE qui nomme le logement le plus
//     concerné (« c'est là que l'information devient une décision ») ;
//   · PAR LOGEMENT puis PAR AGENT, du plus problématique au moins
//     problématique — cliquer une ligne ouvre ses contrôles du mois, et
//     chaque contrôle s'ouvre dans le panneau de détail (photos signées,
//     constat / reprise côte à côte).
//
// UN appel serveur par mois affiché (agrégats serveur, 0 photo, 0 email —
// l'agent arrive en PRÉNOM). Mois vide : l'écran dit la raison.

import { useState, useEffect, useMemo } from 'react'
import { DS } from '../../lib/ui'
import CarteTitrePage from '../../components/CarteTitrePage'
import PanneauDetailControle from '../../components/PanneauDetailControle'
import { fetchControleBilan } from '../../lib/controle-bilan-api'
import { resolveLogementNom } from '../../lib/sousloc-helpers'
import { CATEGORIES_POINTS, humaniserId } from './ControleView'
import {
  ChevronRight, CircleAlert, Home, Users, Repeat2, ClipboardCheck,
} from 'lucide-react'

const TERRA_SOMBRE = '#8f2f1d'
// Le dégradé des barres : la 1re en terracotta plein, plus clair ensuite.
const BARRES = ['#b8412b', '#c1512d', '#cd7a5a', '#dba28a', '#e6bda9', '#f0d4c6']

/** « 2026-08 » → « août 2026 ». */
export function libelleMoisFR(month) {
  const t = Date.parse(`${month}-01T12:00:00Z`)
  if (Number.isNaN(t)) return month
  return new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric', timeZone: 'Europe/Paris' }).format(t)
}
export function moisVoisin(month, pas) {
  const [y, m] = month.split('-').map(Number)
  const d = new Date(Date.UTC(y, m - 1 + pas, 1))
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}
export function moisCourantParis() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris' }).format(new Date()).slice(0, 7)
}

// « deux fois sur trois » — la fraction RÉDUITE, en lettres jusqu'à seize.
const LETTRES = ['zéro', 'une', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept',
  'huit', 'neuf', 'dix', 'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize']
function enLettres(n) { return LETTRES[n] || String(n) }
function pgcd(a, b) { return b ? pgcd(b, a % b) : a }
export function fractionEnLettres(part, total) {
  if (!total || part <= 0) return null
  if (part >= total) return 'à chaque contrôle'
  const d = pgcd(part, total)
  const p = part / d
  const t = total / d
  return `${enLettres(p)} fois sur ${enLettres(t)}`
}

/** « repris en 3 h » — le délai signalement → reprise. */
export function delaiReprise(creeLe, reprisLe) {
  const a = Date.parse(creeLe); const b = Date.parse(reprisLe)
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) return null
  const min = Math.round((b - a) / 60000)
  if (min < 60) return `${min} min`
  const h = Math.round(min / 60)
  if (h < 48) return `${h} h`
  return `${Math.round(h / 24)} j`
}

function libelleCat(id) {
  const c = CATEGORIES_POINTS.find((x) => x.id === id)
  return c ? c.label : humaniserId(id)
}

// Le DÉBUT DE LA PHRASE, accordé par catégorie (« La salle de bain est
// signalée… », « Les sols sont signalés… ») — la maquette parle français.
const DEBUTS_PHRASE = {
  salle_de_bain: 'La salle de bain est signalée',
  cuisine: 'La cuisine est signalée',
  chambres: 'Les chambres sont signalées',
  linge: 'Le linge est signalé',
  sols: 'Les sols sont signalés',
  autre: '« Autre » est signalé',
}
function debutPhrase(cle) {
  return DEBUTS_PHRASE[cle] || `${libelleCat(cle)} est signalée`
}
function dateCourte(iso) {
  const t = Date.parse(String(iso || ''))
  if (Number.isNaN(t)) return ''
  return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', timeZone: 'Europe/Paris' }).format(t)
}

export default function ControleBilan({ cfg }) {
  const moisCourant = moisCourantParis()
  const [mois, setMois] = useState(moisCourant)
  const [donnees, setDonnees] = useState(null)
  const [chargement, setChargement] = useState(true)
  const [erreur, setErreur] = useState(null)
  // La ligne dépliée : { type: 'logement'|'agent', cle } | null.
  const [deplie, setDeplie] = useState(null)
  const [detailId, setDetailId] = useState(null)

  useEffect(() => {
    let annule = false
    setChargement(true); setErreur(null); setDeplie(null)
    fetchControleBilan({ month: mois })
      .then((json) => { if (!annule) setDonnees(json) })
      .catch((e) => { if (!annule) setErreur(e?.message || 'Chargement impossible.') })
      .finally(() => { if (!annule) setChargement(false) })
    return () => { annule = true }
  }, [mois])

  const nomLog = (id) => resolveLogementNom(cfg, id)
  const totaux = donnees?.totaux || { controles: 0, bons: 0, avec_points: 0, en_attente: 0 }
  const pctBons = totaux.controles > 0 ? Math.round((totaux.bons / totaux.controles) * 100) : null

  // LA PHRASE — nomme le logement le plus concerné de la catégorie de tête.
  const phrase = useMemo(() => {
    const cats = donnees?.categories || []
    if (!cats.length) return null
    const top = cats[0]
    const fraction = fractionEnLettres(top.controles, totaux.avec_points)
    if (!fraction) return null
    const bout = top.logement_top
      ? ` — dont ${top.logement_top.points} fois ${top.logement_top.points > 1 ? 'au' : 'au'} ${nomLog(top.logement_top.logement_id)}.`
      : '.'
    return `${debutPhrase(top.cle)} ${fraction}${bout}`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [donnees, totaux.avec_points])

  const etatCourt = (c) => {
    if (c.statut === 'bon') return { texte: 'rien à signaler', clos: true }
    if (c.statut === 'regle') return { texte: `${c.nb_points} point${c.nb_points > 1 ? 's' : ''} · repris`, clos: true }
    return { texte: `${c.nb_points} point${c.nb_points > 1 ? 's' : ''} · en attente`, clos: false }
  }

  // La sous-liste d'une ligne dépliée — les contrôles du mois de ce
  // logement / cet agent, chacun cliquable vers le panneau de détail.
  const sousListe = (type, cle) => {
    const liste = (donnees?.controles || []).filter((c) =>
      type === 'logement' ? c.logement_id === cle : c.agent_prenom === cle)
    return (
      <div data-testid={`bilan-sousliste-${type}-${cle}`}
        style={{ borderTop: '1px solid #eef1f4', padding: '4px 0 6px' }}>
        {liste.map((c) => {
          const e = etatCourt(c)
          return (
            <button key={c.id} type="button" className="fbz-press"
              data-testid={`bilan-controle-${c.id}`}
              onClick={() => setDetailId(c.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                background: 'none', border: 'none', padding: '7px 4px', cursor: 'pointer',
                fontFamily: DS.fontBody, textAlign: 'left' }}>
              <span style={{ fontSize: 11.5, color: DS.textSecondary, width: 88, flexShrink: 0 }}>
                {dateCourte(c.cree_le)}
              </span>
              <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: '#2e3d4d',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {type === 'logement' ? c.agent_prenom : nomLog(c.logement_id)}
              </span>
              <span style={{ flexShrink: 0, fontSize: 10.5, fontWeight: 700,
                padding: '3px 8px', borderRadius: 7,
                background: e.clos ? '#f0f7f3' : '#fdf4f0',
                color: e.clos ? '#1f5a43' : TERRA_SOMBRE }}>
                {e.texte}
              </span>
              <ChevronRight size={14} strokeWidth={2.2} color="#98a2ad" style={{ flexShrink: 0 }} />
            </button>
          )
        })}
      </div>
    )
  }

  const blocBlanc = { background: '#fff', border: '1px solid #d5e3ee', borderRadius: 12,
    padding: '13px 14px', marginBottom: 12 }
  const enTeteBloc = (Ico, fond, titre) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9 }}>
      <span aria-hidden="true" style={{ width: 26, height: 26, borderRadius: 8, background: fond,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <Ico size={14} strokeWidth={2.2} color="#fff" />
      </span>
      <span style={{ fontSize: 11, fontWeight: 700, color: '#233240',
        textTransform: 'uppercase', letterSpacing: '.06em' }}>{titre}</span>
    </div>
  )
  const pastilleEtat = (repris, texteBon) => repris > 0
    ? <span style={{ flexShrink: 0, fontSize: 10.5, fontWeight: 700, padding: '3px 9px',
        borderRadius: 7, background: '#fdf4f0', color: TERRA_SOMBRE }}>{repris} repris</span>
    : <span style={{ flexShrink: 0, fontSize: 10.5, fontWeight: 700, padding: '3px 9px',
        borderRadius: 7, background: '#f0f7f3', color: '#1f5a43' }}>{texteBon}</span>

  return (
    <div data-testid="controle-bilan">
      {/* ══ LE BANDEAU ══ */}
      <CarteTitrePage
        testId="bilan-bandeau"
        titre={(
          <span style={{ fontSize: 20 }}>
            Le bilan{' '}
            <span data-testid="bilan-mois" style={{ fontSize: 11.5, fontWeight: 400,
              fontFamily: DS.fontBody, color: 'rgba(255,255,255,.75)' }}>
              {libelleMoisFR(mois)}
            </span>
          </span>
        )}
        actions={(
          <span style={{ display: 'inline-flex', gap: 6, flexShrink: 0 }}>
            <button type="button" data-testid="bilan-mois-prec" aria-label="Mois précédent"
              onClick={() => setMois((m) => moisVoisin(m, -1))} className="fbz-press"
              style={{ width: 32, height: 32, borderRadius: 8, border: 'none', cursor: 'pointer',
                background: 'rgba(255,255,255,.14)', color: '#fff', fontSize: 15, fontWeight: 700 }}>‹</button>
            <button type="button" data-testid="bilan-mois-suiv" aria-label="Mois suivant"
              onClick={() => setMois((m) => moisVoisin(m, 1))} disabled={mois >= moisCourant}
              className="fbz-press"
              style={{ width: 32, height: 32, borderRadius: 8, border: 'none',
                cursor: mois >= moisCourant ? 'not-allowed' : 'pointer',
                background: 'rgba(255,255,255,.14)', color: '#fff', fontSize: 15, fontWeight: 700,
                opacity: mois >= moisCourant ? .45 : 1 }}>›</button>
          </span>
        )}
        sousLeFilet={(
          <div style={{ display: 'flex', alignItems: 'stretch', gap: 10, flexWrap: 'wrap' }}>
            <div data-testid="bilan-total" style={{ background: 'rgba(255,255,255,.13)',
              borderRadius: 10, padding: '8px 14px', minWidth: 86 }}>
              <span style={{ display: 'block', fontSize: 17, fontWeight: 700, color: '#fff' }}>
                {totaux.controles}
              </span>
              <span style={{ display: 'block', fontSize: 10, color: 'rgba(255,255,255,.75)' }}>
                contrôles
              </span>
            </div>
            <div data-testid="bilan-bons" style={{ background: 'rgba(255,255,255,.13)',
              borderRadius: 10, padding: '8px 14px', minWidth: 106 }}>
              <span style={{ display: 'block', fontSize: 17, fontWeight: 700, color: '#fff' }}>
                {totaux.bons}
                {pctBons != null && (
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#a8e0c4', marginLeft: 6 }}>
                    {pctBons} %
                  </span>
                )}
              </span>
              <span style={{ display: 'block', fontSize: 10, color: 'rgba(255,255,255,.75)' }}>
                rien à signaler
              </span>
            </div>
            <div data-testid="bilan-reprendre" style={{ background: 'rgba(255,255,255,.13)',
              borderRadius: 10, padding: '8px 14px', minWidth: 128 }}>
              <span style={{ display: 'block', fontSize: 17, fontWeight: 700, color: '#fff' }}>
                {totaux.avec_points}
              </span>
              <span style={{ display: 'block', fontSize: 10, color: '#f5c4b4' }}>
                à reprendre{totaux.en_attente > 0 ? ` · ${totaux.en_attente} en attente` : ''}
              </span>
            </div>
          </div>
        )}
      />

      {chargement && <div style={{ padding: 24, textAlign: 'center', color: DS.textMuted, fontSize: 13 }}>Chargement…</div>}
      {erreur && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: 14,
          background: DS.dangerBg, color: DS.dangerText, borderRadius: 10, fontSize: 13 }}>
          <CircleAlert size={16} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} /> {erreur}
        </div>
      )}

      {/* MOIS VIDE — la raison écrite. */}
      {!chargement && !erreur && totaux.controles === 0 && (
        <div data-testid="bilan-vide"
          style={{ padding: '30px 16px', textAlign: 'center', background: '#fff',
            border: '1px solid #d5e3ee', borderRadius: 12, fontSize: 13.5, color: DS.textSecondary }}>
          Aucun contrôle en {libelleMoisFR(mois)}.
        </div>
      )}

      {!chargement && !erreur && totaux.controles > 0 && (
        <>
          {/* ══ CE QUI REVIENT — LE CŒUR DE LA PAGE ══ */}
          {(donnees?.categories || []).length > 0 && (
            <div data-testid="bilan-revient"
              style={{ background: '#fdf9f7', border: '1px solid #e0b5a5', borderRadius: 12,
                padding: '13px 14px', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span aria-hidden="true" style={{ width: 26, height: 26, borderRadius: 8,
                  background: '#b8412b', display: 'inline-flex', alignItems: 'center',
                  justifyContent: 'center' }}>
                  <Repeat2 size={14} strokeWidth={2.2} color="#fff" />
                </span>
                <span style={{ fontSize: 11, fontWeight: 700, color: TERRA_SOMBRE,
                  textTransform: 'uppercase', letterSpacing: '.06em' }}>Ce qui revient</span>
              </div>
              {(() => {
                const cats = donnees.categories
                const max = cats[0]?.points || 1
                return cats.map((c, i) => (
                  <div key={c.cle} data-testid={`bilan-cat-${c.cle}`}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: i < cats.length - 1 ? 8 : 0 }}>
                    <span style={{ width: 96, flexShrink: 0, fontSize: 11.5, fontWeight: 600,
                      color: '#233240', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {libelleCat(c.cle)}
                    </span>
                    <span aria-hidden="true" style={{ flex: 1, height: 8, borderRadius: 4, background: '#eef1f4' }}>
                      <span style={{ display: 'block', height: 8, borderRadius: 4,
                        width: `${Math.max(6, Math.round((c.points / max) * 100))}%`,
                        background: BARRES[Math.min(i, BARRES.length - 1)] }} />
                    </span>
                    <span style={{ flexShrink: 0, fontSize: 12, fontWeight: 700, color: TERRA_SOMBRE }}>
                      {c.points}
                    </span>
                  </div>
                ))
              })()}
              {phrase && (
                <div data-testid="bilan-phrase"
                  style={{ marginTop: 11, background: '#fff', borderRadius: 9,
                    padding: '9px 12px', fontSize: 12, lineHeight: 1.55, color: '#2e3d4d' }}>
                  {phrase}
                </div>
              )}
            </div>
          )}

          {/* ══ PAR LOGEMENT ══ */}
          <div data-testid="bilan-logements" style={blocBlanc}>
            {enTeteBloc(Home, '#3d5166', 'Par logement')}
            {(donnees?.par_logement || []).map((l) => (
              <div key={l.logement_id}>
                <button type="button" className="fbz-press"
                  data-testid={`bilan-logement-${l.logement_id}`}
                  aria-expanded={deplie?.type === 'logement' && deplie.cle === l.logement_id}
                  onClick={() => setDeplie((d) =>
                    d?.type === 'logement' && d.cle === l.logement_id ? null : { type: 'logement', cle: l.logement_id })}
                  style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%',
                    minHeight: 44, background: 'none', border: 'none', padding: '5px 2px',
                    cursor: 'pointer', fontFamily: DS.fontBody, textAlign: 'left' }}>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 700, color: '#233240',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {nomLog(l.logement_id)}
                  </span>
                  <span style={{ flexShrink: 0, fontSize: 11.5, color: DS.textSecondary }}>
                    {l.controles} contrôle{l.controles > 1 ? 's' : ''}
                  </span>
                  {pastilleEtat(l.repris, 'tous bons')}
                </button>
                {deplie?.type === 'logement' && deplie.cle === l.logement_id && sousListe('logement', l.logement_id)}
              </div>
            ))}
          </div>

          {/* ══ PAR AGENT ══ */}
          <div data-testid="bilan-agents" style={blocBlanc}>
            {enTeteBloc(Users, '#5c8ab8', 'Par agent')}
            {(donnees?.par_agent || []).map((a) => (
              <div key={a.prenom}>
                <button type="button" className="fbz-press"
                  data-testid={`bilan-agent-${a.prenom}`}
                  aria-expanded={deplie?.type === 'agent' && deplie.cle === a.prenom}
                  onClick={() => setDeplie((d) =>
                    d?.type === 'agent' && d.cle === a.prenom ? null : { type: 'agent', cle: a.prenom })}
                  style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%',
                    minHeight: 44, background: 'none', border: 'none', padding: '5px 2px',
                    cursor: 'pointer', fontFamily: DS.fontBody, textAlign: 'left' }}>
                  <span aria-hidden="true" style={{ width: 28, height: 28, borderRadius: 9,
                    background: '#5c8ab8', color: '#fff', display: 'inline-flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: 11.5, fontWeight: 700, flexShrink: 0 }}>
                    {String(a.prenom || '?').slice(0, 2).toUpperCase()}
                  </span>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 700, color: '#233240',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {a.prenom}
                  </span>
                  <span style={{ flexShrink: 0, fontSize: 11.5, color: DS.textSecondary }}>
                    {a.controles} ménage{a.controles > 1 ? 's' : ''}
                  </span>
                  {pastilleEtat(a.repris, `${a.bons} bon${a.bons > 1 ? 's' : ''}`)}
                </button>
                {deplie?.type === 'agent' && deplie.cle === a.prenom && sousListe('agent', a.prenom)}
              </div>
            ))}
          </div>

          {/* Le rappel : le geste vit sur « À contrôler ». */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center',
            fontSize: 11, color: DS.textMuted, marginTop: 4 }}>
            <ClipboardCheck size={13} strokeWidth={2} /> Le bilan est en lecture — les gestes vivent sur « À contrôler ».
          </div>
        </>
      )}

      {detailId && (
        <PanneauDetailControle controleId={detailId} onFermer={() => setDetailId(null)} />
      )}
    </div>
  )
}
